import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AccessPersonRow,
  AccessTabs,
  ASSIGNABLE_ROLES,
  InvitePanel,
  PermissionChecklist,
  permissionLabel,
  RoleSelect,
  type Translate,
  UserSelect,
  useCustomRoles,
  useScopeInvitations,
  useTeams,
  useWorkspaceMembers,
} from '@/entities/collab';
import {
  type Database,
  type DatabaseAccess,
  type DatabaseParticipant,
  type DatabaseRole,
  type DatabaseRule,
  isDatabaseOwner,
  type RuleSubjectKind,
  useCreateDatabaseRule,
  useDatabaseAccess,
  useDeleteDatabaseRule,
  useRemoveDatabaseMember,
  useResumeDatabaseRule,
  useSetDatabaseDenial,
  useSetDatabaseExceptions,
  useSetDatabaseMember,
  useSetDatabaseRolePermissions,
} from '@/entities/database';
import { useViewer } from '@/entities/viewer';
import { useWorkspace } from '@/entities/workspace';
import { useErrorMessage } from '@/shared/i18n';
import {
  Button,
  Combobox,
  Dialog,
  ErrorState,
  HelpRow,
  IconButton,
  Icons,
  LoadingState,
  useToast,
} from '@/shared/ui';

import styles from './DatabaseAdmin.module.css';

type Tab = 'people' | 'invitations' | 'rules' | 'roles';
const TABS: readonly Tab[] = ['people', 'invitations', 'rules', 'roles'];

function useReport() {
  const toast = useToast();
  const message = useErrorMessage();
  return (error: unknown) => {
    toast.show({ message: message(error) });
  };
}

function ExceptionsDialog({
  databaseId,
  access,
  member,
  onClose,
}: {
  databaseId: string;
  access: DatabaseAccess;
  member: { user_id: string; display_name: string };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const tr = t as unknown as Translate;
  const set = useSetDatabaseExceptions(databaseId);
  const message = useErrorMessage();
  const current = access.exceptions.find((e) => e.user_id === member.user_id);
  const [state, setState] = useState<Record<string, 'default' | 'grant' | 'revoke'>>(() =>
    Object.fromEntries(
      access.catalog.map((p) => [
        p,
        current?.granted.includes(p)
          ? 'grant'
          : current?.revoked.includes(p)
            ? 'revoke'
            : 'default',
      ]),
    ),
  );
  return (
    <Dialog
      title={t('databases.access.exceptionsFor', { name: member.display_name })}
      onClose={onClose}
    >
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          set.mutate(
            {
              userId: member.user_id,
              granted: access.catalog.filter((p) => state[p] === 'grant'),
              revoked: access.catalog.filter((p) => state[p] === 'revoke'),
            },
            { onSuccess: onClose },
          );
        }}
      >
        <p className={styles.text}>{t('databases.access.exceptionsHint')}</p>
        <ul className={styles.list}>
          {access.catalog.map((permission) => (
            <li key={permission} className={styles.row}>
              <span className={styles.grow}>{permissionLabel(tr, permission)}</span>
              <select
                className={styles.select}
                aria-label={permissionLabel(tr, permission)}
                value={state[permission] ?? 'default'}
                onChange={(event) => {
                  setState((s) => ({
                    ...s,
                    [permission]: event.target.value as 'default' | 'grant' | 'revoke',
                  }));
                }}
              >
                <option value="default">{t('databases.access.byRole')}</option>
                <option value="grant">{t('databases.access.grant')}</option>
                {!access.required.includes(permission) && (
                  <option value="revoke">{t('databases.access.revoke')}</option>
                )}
              </select>
            </li>
          ))}
        </ul>
        {set.error !== null && (
          <p className={styles.error} role="alert">
            {message(set.error)}
          </p>
        )}
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={set.isPending}>
            {t('common.save')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

const SOURCE_KEYS = {
  owner: 'workspaceOwner',
  database_owner: 'databaseOwner',
  direct: 'direct',
  workspace_role: 'viaWorkspaceRole',
  custom_role: 'viaCustomRole',
  team: 'viaTeam',
  confidential_view: 'powerView',
  confidential_edit: 'powerEdit',
  confidential_access: 'powerAccess',
} as const;

/** Why somebody has access, in the words of the panel ("Direct assignment · Via a team"). */
function reasonOf(t: (key: string) => string, person: DatabaseParticipant): string {
  return person.sources
    .map((source) => t(`databases.access.source.${SOURCE_KEYS[source]}`))
    .join(' · ');
}

function PeopleTab({ database, access }: { database: Database; access: DatabaseAccess }) {
  const { t } = useTranslation();
  const tr = t as unknown as (key: string) => string;
  const report = useReport();
  const viewer = useViewer();
  const viewerId = viewer.status === 'authenticated' ? viewer.user.id : null;
  const members = useWorkspaceMembers(database.workspace_id);
  const setRole = useSetDatabaseMember(database.id);
  const removeMember = useRemoveDatabaseMember(database.id);
  const denial = useSetDatabaseDenial(database.id);
  const [pick, setPick] = useState<string | null>(null);
  const [role, setNewRole] = useState<DatabaseRole>(access.assignable_roles[0] ?? 'member');
  const [exceptionsFor, setExceptionsFor] = useState<{
    user_id: string;
    display_name: string;
  } | null>(null);
  const reached = new Set(access.participants.map((p) => p.user_id));
  const taken = new Set([...reached, ...access.denials.map((d) => d.user_id)]);
  const canAdd = access.assignable_roles.length > 0;
  const busy = setRole.isPending || removeMember.isPending || denial.isPending;

  return (
    <div className={styles.stack}>
      <ul className={styles.list}>
        {access.participants.map((person) => {
          const isSelf = person.user_id === viewerId;
          const editable = person.manageable && person.role !== null && !isSelf;
          return (
            <AccessPersonRow
              key={person.user_id}
              userId={person.user_id}
              name={person.display_name}
              suffix={isSelf ? `(${t('access.you')})` : undefined}
              reason={
                <>
                  {reasonOf(tr, person)}
                  {!person.can_view && ` · ${t('databases.access.noContents')}`}
                  {person.denied && ` · ${t('databases.access.deniedButPowers')}`}
                  {person.exceptions > 0 &&
                    ` · ${t('databases.access.exceptionsCount', { count: person.exceptions })}`}
                </>
              }
            >
              {person.manageable && !isSelf && person.has_direct && person.role ? (
                <RoleSelect
                  label={t('databases.access.roleOf', { name: person.display_name })}
                  value={person.role}
                  roles={access.assignable_roles}
                  disabled={busy}
                  onChange={(next) => {
                    setRole.mutate({ userId: person.user_id, role: next }, { onError: report });
                  }}
                />
              ) : (
                <span>{person.role ? t(`roles.${person.role}`) : '—'}</span>
              )}
              {person.manageable && !isSelf && person.has_direct && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => {
                    removeMember.mutate(person.user_id, { onError: report });
                  }}
                >
                  {t('databases.access.removeDirect')}
                </Button>
              )}
              {editable && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setExceptionsFor(person);
                  }}
                >
                  {t('databases.access.exceptions')}
                </Button>
              )}
              {person.manageable && !isSelf && !person.denied && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => {
                    denial.mutate({ userId: person.user_id, denied: true }, { onError: report });
                  }}
                >
                  {t('databases.access.deny')}
                </Button>
              )}
            </AccessPersonRow>
          );
        })}
      </ul>

      {canAdd && (
        <form
          className={styles.addRow}
          onSubmit={(event) => {
            event.preventDefault();
            if (pick === null) return;
            setRole.mutate(
              { userId: pick, role },
              {
                onSuccess: () => {
                  setPick(null);
                },
                onError: report,
              },
            );
          }}
        >
          <UserSelect
            label={t('databases.access.addPerson')}
            placeholder={t('databases.access.addPerson')}
            users={(members.data ?? []).filter((m) => !taken.has(m.user_id))}
            value={pick}
            onChange={setPick}
          />
          <RoleSelect
            label={t('databases.access.role')}
            value={role}
            roles={access.assignable_roles}
            size="md"
            onChange={setNewRole}
          />
          <Button type="submit" variant="primary" disabled={pick === null || setRole.isPending}>
            {t('databases.access.add')}
          </Button>
        </form>
      )}
      <p className={styles.muted}>{t('databases.access.removeVsDeny')}</p>

      {access.denials.length > 0 && (
        <section aria-labelledby="db-denials" className={styles.subsection}>
          <h4 id="db-denials" className={styles.subTitle}>
            {t('databases.access.denied')}
          </h4>
          <ul className={styles.list}>
            {access.denials.map((d) => (
              <AccessPersonRow key={d.user_id} userId={d.user_id} name={d.display_name}>
                <span className={styles.grow} />
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => {
                    denial.mutate({ userId: d.user_id, denied: false }, { onError: report });
                  }}
                >
                  {t('databases.access.lift')}
                </Button>
              </AccessPersonRow>
            ))}
          </ul>
        </section>
      )}

      {exceptionsFor && (
        <ExceptionsDialog
          databaseId={database.id}
          access={access}
          member={exceptionsFor}
          onClose={() => {
            setExceptionsFor(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * Invitations to a database role, in the same panel as board invitations: invite a member of the
 * workspace, see who was invited by whom and when, resend or cancel. An invitation grants nothing
 * until it is accepted; accepting adds a DIRECT role (shown under People). The roles offered are the
 * ones the server will accept from this viewer.
 */
function InvitationsTab({ database, access }: { database: Database; access: DatabaseAccess }) {
  const { t } = useTranslation();
  return (
    <div className={styles.stack}>
      <p className={styles.text}>{t('databases.access.invitationsHint')}</p>
      <InvitePanel
        scope={{ kind: 'database', id: database.id }}
        allowedRoles={access.assignable_roles}
        canInvite={access.assignable_roles.length > 0}
      />
    </div>
  );
}

function ruleSubject(t: (key: string) => string, rule: DatabaseRule): string {
  if (rule.subject_kind === 'role' && rule.subject_role) return t(`roles.${rule.subject_role}`);
  return rule.subject_name ?? '…';
}

function RulesTab({ database, access }: { database: Database; access: DatabaseAccess }) {
  const { t } = useTranslation();
  const tr = t as unknown as (key: string) => string;
  const report = useReport();
  const isTeam = useWorkspace(database.workspace_id).data?.workspace_type === 'team';
  const teams = useTeams(database.workspace_id, isTeam);
  const customRoles = useCustomRoles(database.workspace_id, isTeam);
  const create = useCreateDatabaseRule(database.id);
  const remove = useDeleteDatabaseRule(database.id);
  const resume = useResumeDatabaseRule(database.id);
  const [kind, setKind] = useState<RuleSubjectKind>('role');
  const [subject, setSubject] = useState<string | null>(null);
  const [role, setRole] = useState<DatabaseRole>('member');

  const subjects = useMemo(() => {
    if (kind === 'team') return (teams.data ?? []).map((x) => ({ value: x.id, label: x.name }));
    if (kind === 'custom_role') {
      return (customRoles.data ?? []).map((x) => ({ value: x.id, label: x.name }));
    }
    return ASSIGNABLE_ROLES.map((r) => ({ value: r, label: tr(`roles.${r}`) }));
  }, [kind, teams.data, customRoles.data, tr]);

  return (
    <div className={styles.stack}>
      <p className={styles.text}>
        {database.is_confidential
          ? t('databases.access.rulesConfidential')
          : t('databases.access.rulesHint')}
      </p>
      {access.rules.length === 0 && <p className={styles.muted}>{t('databases.access.noRules')}</p>}
      <ul className={styles.list}>
        {access.rules.map((rule) => (
          <li key={rule.id} className={styles.person}>
            <span className={styles.personName}>
              {t(`databases.access.kind.${rule.subject_kind}`)}: {ruleSubject(tr, rule)} →{' '}
              {t(`roles.${rule.role}`)}
              {rule.suspended && (
                <span className={styles.badgeWarn}>{t('databases.access.suspended')}</span>
              )}
            </span>
            <span className={styles.personTools}>
              {rule.suspended && !database.is_confidential && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    resume.mutate(rule.id, { onError: report });
                  }}
                >
                  {t('databases.access.resume')}
                </Button>
              )}
              <IconButton
                label={t('databases.access.deleteRule')}
                icon={<Icons.trash />}
                onClick={() => {
                  remove.mutate(rule.id, { onError: report });
                }}
              />
            </span>
          </li>
        ))}
      </ul>
      {!database.is_confidential && (
        <form
          className={styles.addRow}
          onSubmit={(event) => {
            event.preventDefault();
            if (subject === null) return;
            create.mutate(
              kind === 'role'
                ? { subject_kind: 'role', subject_role: subject as DatabaseRole, role }
                : kind === 'team'
                  ? { subject_kind: 'team', team_id: subject, role }
                  : { subject_kind: 'custom_role', custom_role_id: subject, role },
              {
                onSuccess: () => {
                  setSubject(null);
                },
                onError: report,
              },
            );
          }}
        >
          <Combobox
            label={t('databases.access.subjectKind')}
            value={kind}
            options={(isTeam
              ? (['role', 'team', 'custom_role'] as const)
              : (['role'] as const)
            ).map((value) => ({ value, label: t(`databases.access.kind.${value}`) }))}
            onChange={(value) => {
              setKind(value as RuleSubjectKind);
              setSubject(null);
            }}
          />
          <Combobox
            label={t('databases.access.subject')}
            value={subject}
            options={subjects}
            placeholder={t('databases.choose')}
            onChange={setSubject}
          />
          <RoleSelect
            label={t('databases.access.role')}
            value={role}
            roles={ASSIGNABLE_ROLES}
            size="md"
            onChange={setRole}
          />
          <Button type="submit" variant="primary" disabled={subject === null || create.isPending}>
            {t('databases.access.addRule')}
          </Button>
        </form>
      )}
    </div>
  );
}

function RolesTab({ database, access }: { database: Database; access: DatabaseAccess }) {
  const { t } = useTranslation();
  const report = useReport();
  const save = useSetDatabaseRolePermissions(database.id);
  const owner = isDatabaseOwner(database);
  const grantable = owner ? null : new Set(database.my_permissions);
  return (
    <div className={styles.stack}>
      <HelpRow
        topic={t('databases.access.title', { name: database.name })}
        topicId="database-access"
      >
        <p className={styles.text}>{t('databases.access.rolesHint')}</p>
      </HelpRow>
      {access.roles
        .filter((r) => r.role !== 'owner')
        .map((template) => (
          <section
            key={template.role}
            className={styles.subsection}
            aria-label={t(`roles.${template.role}`)}
          >
            <h4 className={styles.subTitle}>{t(`roles.${template.role}`)}</h4>
            <PermissionChecklist
              groups={{ database: access.catalog }}
              value={new Set(template.permissions)}
              required={new Set(access.required)}
              defaults={new Set(template.defaults)}
              disabled={!template.editable || save.isPending}
              grantable={grantable}
              onChange={(next) => {
                save.mutate({ role: template.role, permissions: [...next] }, { onError: report });
              }}
            />
          </section>
        ))}
    </div>
  );
}

/** Who may do what in this database: people, rules (normal databases) and the role matrix. */
export function DatabaseAccessDialog({
  database,
  onClose,
}: {
  database: Database;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const access = useDatabaseAccess(database.id);
  const pending = useScopeInvitations({ kind: 'database', id: database.id }).data?.length ?? 0;
  const [tab, setTab] = useState<Tab>('people');
  return (
    <Dialog
      title={t('databases.access.title', { name: database.name })}
      size="lg"
      onClose={onClose}
    >
      <AccessTabs
        tabs={TABS.map((value) => ({
          id: value,
          label:
            value === 'invitations' && pending > 0
              ? t('databases.access.tabs.invitationsCount', { count: pending })
              : t(`databases.access.tabs.${value}`),
        }))}
        value={tab}
        onChange={(next) => {
          setTab(next);
          // Somebody may have accepted meanwhile: the participants are current when shown.
          if (next === 'people') void access.refetch();
        }}
        label={t('databases.access.sections')}
      />
      {database.is_confidential && (
        <p className={styles.note}>
          <Icons.lock /> {t('databases.access.confidentialNote')}
        </p>
      )}
      {access.isPending && <LoadingState />}
      {access.isError && <ErrorState error={access.error} onRetry={() => void access.refetch()} />}
      {access.isSuccess && (
        <div role="tabpanel">
          {tab === 'people' && <PeopleTab database={database} access={access.data} />}
          {tab === 'invitations' && <InvitationsTab database={database} access={access.data} />}
          {tab === 'rules' && <RulesTab database={database} access={access.data} />}
          {tab === 'roles' && <RolesTab database={database} access={access.data} />}
        </div>
      )}
    </Dialog>
  );
}
